import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import _ from "lodash";
import { RootState } from "redux/store";
import {
  initialParticular,
  initialState,
  IState,
  OtherParticularChangeType,
  ParticularChangeType,
} from "./types";
import { getEstimatedDueDate } from "./utils";
import { placeOfSupplyStates } from "data/placeOfSupply";

export const createInvoiceSlice = createSlice({
  name: "createInvoice",
  initialState: _.cloneDeep(initialState),
  reducers: {
    resetState(state: IState) {
      let stateCopy = _.cloneDeep(initialState);
      Object.keys(stateCopy).forEach((key) => {
        state[key] = stateCopy[key];
      });
    },
    handleFieldChange(state: IState, action: PayloadAction<any>) {
      const { key, value } = action.payload;
      state[key] = value;
      if (key === 'hasTds' && !value) {
        state.tdsRate = null;
        state.tdsSection = null;
        state.tdsView = false;
      }
      if (key === 'hasTds' && value) {
        state.tdsRate = '10';
        state.tdsSection = "194J";

      }
      if (key === "terms") {
        state.estimateDueDate = getEstimatedDueDate(
          value,
          state.estimateDate,
          state.estimateDueDate
        );
      }
      if (key === "estimateDate") {
        state.estimateDueDate = getEstimatedDueDate(
          state["terms"],
          state.estimateDate,
          state.estimateDueDate
        );
      }
      if (key === 'selected') {
        const valueArray = Array.isArray(value) ? value : [value]; // Ensure `value` is always an array
        const existingIds = new Set(state.selected.map(item => item.id)); // Collect existing IDs in a Set
        const uniqueValues = valueArray.filter(item => !existingIds.has(item.id)); // Filter out duplicates
        state.selected = [...state.selected, ...uniqueValues]; // Merge existing and new unique values
      }
    },
    handlePlaceOfSupplyChange(state: IState, action: PayloadAction<any>) {
      state.placeOfSupply = action.payload;
      state.interState = action.payload.split("-")[1] === state.billingEntityAddress?.state;
    },
    handleBillingEntityChange(state: IState, action: PayloadAction<{ billingEntity: any }>) {
      let billEntity = action.payload.billingEntity;
      state.billingEntity = billEntity?.id;
      let address = {
        tag: billEntity?.tag,
        legalName: billEntity?.tradeName,
        displayName: billEntity?.displayName,
        buildingNumber: billEntity?.buildingNumber,
        floorNumber: billEntity?.floorNumber,
        location: billEntity?.location,
        district: billEntity?.district,
        buildingName: billEntity?.buildingName,
        street: billEntity?.street,
        city: billEntity?.city,
        state: billEntity?.state,
        pincode: billEntity?.pincode,
        email: billEntity?.email,
        primaryContactEmail : billEntity?.primaryContactEmail,
        mobileNumber: billEntity?.mobileNumber,
        billingfulladdress: billEntity?.address,
        countryCode: billEntity?.countryCode,
        msmeType: billEntity?.msmeType ?? null,
        msmeNumber: billEntity?.msmeNumber ?? null,
      };
      state.isZohoConnect = billEntity?.isZohoConnect && !billEntity?.isEInvoiceEnabled;
      state.billingEntityAddress = address;
      state.interState = state.placeOfSupply === billEntity?.state;
      if (state.fromTask) {

        if (state?.client?.gstVerified) {
          const stateNumber = state?.client?.gstNumber.slice(0, 2);
          const placeOfSupply = placeOfSupplyStates.find(
            (state) => state.slice(0, 2) === stateNumber
          );

          if (placeOfSupply) {
            state.placeOfSupply = placeOfSupply;
            state.interState = stateNumber === state.billingEntityAddress?.state;
          }
        } else {
          state.placeOfSupply = state?.type === 'INVOICE' ? state.locationOfSupply : state.proformaLocationOfSupply;
          state.interState = state.placeOfSupply === state.billingEntityAddress?.state;
        }
      }
    },

    handleApprovalChange(state: IState, action: PayloadAction<any>) {
      state.approvalHierarchy = action.payload;
    },
    handleClientChange(state: IState, action: PayloadAction<{ client: any }>) {
      let client = action.payload.client;
      state.client = client?.id;
      state.clientType = client?.type;
      if (client?.address?.billingfulladdress) {
        const tempBillingAddress = JSON.parse(JSON.stringify(client?.address));
        const tempShippingAddress = JSON.parse(JSON.stringify(client?.address));
        tempBillingAddress["mobileNumber"] = client?.mobileNumber;
        tempBillingAddress["email"] = client?.email;
        tempShippingAddress["legalName"] = client?.tradeName ? client?.tradeName : client?.legalName ? client?.legalName : client?.displayName;
        tempBillingAddress["displayName"] = client?.tradeName || client?.displayName;
        tempShippingAddress["mobileNumber"] = client?.mobileNumber;
        tempShippingAddress["email"] = client?.email;
        tempShippingAddress["countryCode"] = client?.countryCode;
        state.shippingAddress = tempShippingAddress;
        state.billingAddress = tempShippingAddress;
      } else {
        const tempBillingAddress = {
          displayName: "",
          legalName: "",
          tag: "",
          buildingNumber: "",
          floorNumber: "",
          district: "",
          location: "",
          buildingName: "",
          street: "",
          city: "",
          state: "",
          pincode: "",
          email: "",
          mobileNumber: "",
          billingfulladdress: "",
          countryCode: "",
        };
        tempBillingAddress["mobileNumber"] = client?.mobileNumber;
        tempBillingAddress["email"] = client?.email;
        tempBillingAddress["legalName"] = client?.tradeName ? client?.tradeName : client?.legalName ? client?.legalName : client?.displayName;
        tempBillingAddress["displayName"] = client?.tradeName || client?.displayName;
        tempBillingAddress["countryCode"] = client?.countryCode;

        state.billingAddress = tempBillingAddress;
      }
      if (client?.gstVerified) {
        const stateNumber = client?.gstNumber.slice(0, 2);
        const placeOfSupply = placeOfSupplyStates.find(
          (state) => state.slice(0, 2) === stateNumber
        );

        if (placeOfSupply) {
          state.placeOfSupply = placeOfSupply;
          state.interState = stateNumber === state.billingEntityAddress?.state;
        }
      } else {
        state.placeOfSupply = state?.type === 'INVOICE' ? state.locationOfSupply : state.proformaLocationOfSupply;
        state.interState = state.placeOfSupply === state.billingEntityAddress?.state;
      }
    },
    handleExistingClientChange(state: IState, action: PayloadAction<any>) {
      let client = action.payload.client || action.payload.clientGroup;
      state.client = client?.id;
      if (client?.address?.billingfulladdress) {
        const tempBillingAddress = JSON.parse(JSON.stringify(client));
        const tempShippingAddress = JSON.parse(JSON.stringify(client));
        tempBillingAddress["mobileNumber"] = client?.mobileNumber;
        tempBillingAddress["email"] = client?.email;
        tempBillingAddress["displayName"] = client?.tradeName || client?.displayName;
        tempShippingAddress["mobileNumber"] = client?.mobileNumber;
        tempShippingAddress["email"] = client?.email;
        tempShippingAddress["legalName"] = client?.tradeName ? client?.tradeName : client?.legalName ? client?.legalName : client?.displayName;
        tempShippingAddress["countryCode"] = client?.countryCode;

        state.shippingAddress = tempBillingAddress;
        state.billingAddress = tempShippingAddress;
      } else {
        const tempBillingAddress = {
          displayName: "",
          legalName: "",
          tag: "",
          buildingNumber: "",
          floorNumber: "",
          district: "",
          location: "",
          buildingName: "",
          street: "",
          city: "",
          state: "",
          pincode: "",
          email: "",
          mobileNumber: "",
          billingfulladdress: "",
          countryCode: "",
        };
        tempBillingAddress["mobileNumber"] = client?.mobileNumber;
        tempBillingAddress["email"] = client?.email;
        tempBillingAddress["legalName"] = client?.tradeName ? client?.tradeName : client?.legalName ? client?.legalName : client?.displayName;
        tempBillingAddress["displayName"] = client?.tradeName || client?.displayName;
        tempBillingAddress["countryCode"] = client?.countryCode;

        state.billingAddress = tempBillingAddress;
      }
    },
    handleBankDetailsChange(state: IState, action: PayloadAction<any>) {
      const data = action.payload;
      state.bankDetails = {
        accountNumber: data?.accountNumber,
        bankName: data?.bankName,
        branchName: data?.branchName,
        ifscCode: data?.ifscCode,
        upiAttachmentId: data?.upiStorage?.id,
        upiId: data?.upiId,
        accountName: data?.accountName,
        accountType: data?.accountType,
        upiAttachment: data?.upiStorage?.fileUrl,
        upiEnabled: data?.upiEnabled

      };
    },
    handleSignatureChange(state: IState, action: PayloadAction<any>) {
      const data = action.payload;
      state.signatureUser = data.id;
    },

    handleWhatsapp(state: IState, action: PayloadAction<any>) {
      const data = action.payload;
      state.whatsappCheck = data;
    },

    handleMail(state: IState, action: PayloadAction<any>) {
      const data = action.payload;
      state.emailCheck = data;
    },

    handleResetParticular(state: IState) {
      state.particulars.length = 0;
    },
    handleAddParticular(state: IState) {
      state.particulars.push({ ...initialParticular });
    },
    handleExistingParticular(state: IState, action: any) {
      state.particulars.push(action.payload);
    },
    handleRemoveParticular(
      state: IState,
      action: PayloadAction<{ index: number; id: number | undefined }>
    ) {
      state.particulars.splice(action.payload.index, 1);
      state.selected = state.selected.filter((item) => !action.payload.id);
    },
    handleChangeParticular(state: IState, action: PayloadAction<ParticularChangeType>) {
      const { index, key, value } = action.payload;
      state.particulars[index][key] = value;
      if (key === 'actualFee' || key === 'additionalFee') {
        state.particulars[index]['rate'] = (Number(state.particulars[index].actualFee) || 0) + (Number(state.particulars[index].additionalFee) || 0);
      }
    },
    handleExistingOtherParticular(state: IState, action: PayloadAction<OtherParticularChangeType>) {
      const { id, index, key, value, taskExpenseType } = action.payload;
      state.otherParticulars.push({
        id: id,
        taskExpenseType: "PURE_AGENT",
        name: key,
        amount: value,
      });
    },
    handleAddOtherParticular(state: IState) {
      state.otherParticulars.push({ taskExpenseType: "PURE_AGENT", name: "", amount: 0 });
    },
    handleRemoveOtherParticular(state: IState, action: PayloadAction<number>) {
      state.otherParticulars.splice(action.payload, 1);
    },
    handleChangeOtherParticular(state: IState, action: PayloadAction<OtherParticularChangeType>) {
      const { index, key, value, taskExpenseType } = action.payload;
      state.otherParticulars[index][key] = value;
    },
    handleExistingTermsAndConditions(state: IState, action: PayloadAction<string>) {
      state.termsAndConditionsCopy = state.termsAndConditionsCopy.concat(action.payload);
    },
    handleAddTermsAndConditions(state: IState, action: PayloadAction<string>) {
      state.termsAndConditionsCopy.push(action.payload);
    },
    handleRemoveTermsAndConditions(state: IState, action: PayloadAction<number>) {
      state.termsAndConditionsCopy.splice(action.payload, 1);
    },
    handleUpdateTermsAndConditions(state: IState, action) {
      state.termsAndConditionsCopy = action.payload;
    },

    handleResetTaskParticular(state: IState) {
      // state.particulars = state.particulars.filter(particular => !particular?.id);
      state.particulars = state.particulars.filter(p => !p.taskItemType);
    },


    // handleAddTasksToParticular(state: IState, action: PayloadAction<any[]>) {
    //   for (let task of action.payload) {
    //     state.particulars.push({
    //       ...initialParticular,
    //       name: task.name,
    //       rate: +task?.actualFeeAmount + (task?.additionalexpenditure || 0) || 0,
    //       taskId: task.id,
    //       id: task.id,
    //       taskName: task.name,
    //       expectedFee: +task?.actualFeeAmount || 0,
    //       actualFee: +task?.actualFeeAmount || 0,
    //       status: task.status,
    //       additionalFee: task.additionalexpenditure || 0,
    //       taskNumber: task.taskNumber



    //     });
    //     // let additional = _.filter(task?.expenditure, { type: "ADDITIONAL" });
    //     let pureAgent = _.filter(task?.expenditure, { type: "PURE_AGENT" });
    //     // additional.forEach((expenditure: any) => {
    //     //   state.particulars.push({
    //     //     ...initialParticular,
    //     //     rate: +expenditure?.amount || 0,
    //     //     name: expenditure.particularName,
    //     //     taskId: task.id,
    //     //   });
    //     // });
    //     pureAgent.forEach((expenditure: any) => {
    //       state.otherParticulars.push({
    //         id: expenditure.id,
    //         taskExpenseType: "PURE_AGENT" || expenditure.particularType,
    //         name: expenditure.particularName,
    //         amount: +expenditure?.amount || 0,
    //       });
    //     });
    //   }
    // },

    handleAddTasksToParticular(state: IState, action: PayloadAction<any[]>) {
      for (const task of action.payload) {
        /* ---------- SINGLE LINE ---------- */
        if (task.taskItemType === "SINGLE_LINE") {
          state.particulars.push({
            ...initialParticular,
            taskItemType: "SINGLE_LINE",
            id: task.id,
            taskId: task.id,
            taskNumber: task.taskNumber,
            name: task.taskName,
            taskName: task.taskName,
            expectedFee: +task.expectedFee || 0,
            actualFee: +task.actualFee || 0,
            additionalFee: +task.additionalFee || 0,
            rate: +task.actualFee,
            status: task.status,
          });
        }

        /* ---------- ⭐ MULTIPLE LINE ---------- */
        if (task.taskItemType === "MULTIPLE_LINE") {
          state.particulars.push({
            ...initialParticular,
            taskItemType: "MULTIPLE_LINE",
            name: "",
            rate: task.feeAmount,
            tasks: task.tasks, // ⭐ STORE ALL TASKS HERE
          });
        }
      }
    },
    handleDivideTaxChange(state: IState) {
      state.divideTax = !state.divideTax;
    },
    handleRemoveTaskFromParticular(
      state: IState,
      action: PayloadAction<{ index: number; taskId: number }>
    ) {
      const { index, taskId } = action.payload;
      const particular = state.particulars[index];

      if (particular && particular.tasks) {
        // Remove the task
        particular.tasks = particular.tasks.filter((t: any) => t.id !== taskId);

        // If no tasks left, remove the particular
        if (particular.tasks.length === 0) {
          state.particulars.splice(index, 1);
        } else {
          // Recalculate rate
          const totalRate = particular.tasks.reduce(
            (sum: number, t: any) => sum + (Number(t.actualFee) || 0),
            0
          );
          particular.rate = totalRate;
        }
      }
    },
    handleChangeTaskInParticular(
      state: IState,
      action: PayloadAction<{ index: number; taskId: number; key: string; value: any }>
    ) {
      const { index, taskId, key, value } = action.payload;
      const particular = state.particulars[index];

      if (particular) {
        if (particular.taskItemType === "SINGLE_LINE" && particular.taskId === taskId) {
          particular[key] = value;
          if (key === 'actualFee' || key === 'additionalFee') {
            particular.rate = (Number(particular.actualFee) || 0) + (Number(particular.additionalFee) || 0);
          }
        } else if (particular.tasks) {
          const task = particular.tasks.find((t: any) => t.id === taskId);
          if (task) {
            task[key] = value;

            // Recalculate rate
            const totalRate = particular.tasks.reduce(
              (sum: number, t: any) => sum + (Number(t.actualFee) || 0),
              0
            );
            particular.rate = totalRate;
          }
        }
      }
    },
    handleAppendTasksToParticular(
      state: IState,
      action: PayloadAction<{ index: number; tasks: any[] }>
    ) {
      const { index, tasks } = action.payload;
      const particular = state.particulars[index];

      if (particular && particular.taskItemType === "MULTIPLE_LINE") {
        if (!particular.tasks) particular.tasks = [];

        // Filter out tasks that are already in this particular (just in case)
        const existingIds = new Set(particular.tasks.map((t: any) => t.id));
        const newTasks = tasks.filter(t => !existingIds.has(t.id));

        particular.tasks.push(...newTasks);

        // Recalculate rate
        const totalRate = particular.tasks.reduce(
          (sum: number, t: any) => sum + (Number(t.actualFee) || 0),
          0
        );
        particular.rate = totalRate;
      }
    },
  },
});

export const selectInvoice = (state: RootState) => state.createInvoice;

export const {
  handleFieldChange,
  handleBillingEntityChange,
  handleApprovalChange,
  handleAddParticular,
  handleRemoveParticular,
  handleChangeParticular,
  handleExistingParticular,
  handleResetParticular,
  handleBankDetailsChange,
  handleSignatureChange,
  handleAddTermsAndConditions,
  handleExistingTermsAndConditions,
  handleRemoveTermsAndConditions,
  handleUpdateTermsAndConditions,
  handleAddOtherParticular,
  handleExistingOtherParticular,
  handleRemoveOtherParticular,
  handleChangeOtherParticular,
  handleClientChange,
  handleExistingClientChange,
  handleAddTasksToParticular,
  handlePlaceOfSupplyChange,
  resetState,
  handleWhatsapp,
  handleMail,
  handleDivideTaxChange,
  handleResetTaskParticular,
  handleRemoveTaskFromParticular,
  handleChangeTaskInParticular,
  handleAppendTasksToParticular,
} = createInvoiceSlice.actions;

export default createInvoiceSlice.reducer;
